import React, { useState } from 'react';
import Loading from 'src/components/common/Loading';

import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import { productUpgradeRequest } from './api';
import { CheckmarkSolidCircle } from 'src/components/SvgIcons/CheckmarkSolidCircle';
import {
  SlDialog,
  SlDialogCloseButton,
  SlDialogContent,
  SlDialogRoot
} from 'src/components/BeaconV2Components/SlDialog';
import { BeaconBasicUpgrade } from 'src/components/SvgIcons';
import { getAppName } from 'src/utils/app';
import _startCase from 'lodash/startCase';
import { create } from 'zustand';
import { AppName } from 'sl-api-connector';
import { AtlasBasicUpgrade } from 'src/components/SvgIcons/SvgIcons';
import { useAppSelector } from 'src/utils/Hooks';
import colors from 'src/utils/colors';
import { GenericModalStore } from './types';

/**
 * Simple Zustand store for the delete Beacon Basic modal actions.
 */
const useBeaconBasicUpgradeModal = create<GenericModalStore>((set) => ({
  isOpen: false,
  openModal: () => set({ isOpen: true }),
  closeModal: () => set({ isOpen: false })
}));

/**
 * Modal used for Beacon Pro upgrade
 * Controlled via the useBeaconBasicUpgradeModal hook
 */
const BeaconBasicUpgradeModal = () => {
  const appName = getAppName();
  const { isOpen, closeModal } = useBeaconBasicUpgradeModal();
  const userEmail = useAppSelector((state) => state.user.session?.email);
  const [showSuccess, setShowSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  return (
    <SlDialogRoot>
      <SlDialog
        open={isOpen}
        onClose={() => closeModal()}
        width="752px"
        height="624px"
        slotProps={{
          backdrop: {
            style: {
              backdropFilter: 'blur(5px)',
              backgroundColor: 'rgba(5, 40, 73, 0.4)'
            }
          }
        }}
      >
        <SlDialogCloseButton
          onClick={() => closeModal()}
          style={{
            height: '24px',
            width: '24px'
          }}
        />
        <SlDialogContent
          sx={{
            paddingLeft: '13px',
            paddingRight: '0px',
            py: '64px',
            display: 'flex',
            flexDirection: 'column',
            gap: '40px'
          }}
        >
          <Flex
            alignItems="center"
            justifyContent="center"
            flexDirection="column"
            sx={{
              gap: '8px'
            }}
          >
            <div
              style={{
                fontSize: 28,
                fontWeight: 700,
                color: colors.darkBlue
              }}
            >
              View this feature on {_startCase(appName)} Pro.
            </div>
            <div
              style={{
                fontSize: 16,
                fontWeight: 400,
                color: colors.darkBlue
              }}
            >
              Unlock new data and insights when you upgrade.
            </div>
          </Flex>
          <Flex flex={1} alignItems="center" justifyContent="center">
            {appName === AppName.Beacon ? (
              <BeaconBasicUpgrade width={360} height={312} />
            ) : (
                <AtlasBasicUpgrade width={360} height={312} />
              )}
          </Flex>
          <Flex justifyContent="center" alignItems="center" gap="md">
            <SlButton
              variant="contained"
              onClick={async () => {
                setLoading(true);
                await productUpgradeRequest(userEmail, appName);
                setShowSuccess(true);
                setLoading(false);
              }}
              buttonSx={{
                width: '151px !important',
                display: 'flex !important',
                alignItems: 'center !important'
              }}
              textSx={{
                display: 'flex',
                alignItems: 'center'
              }}
            >
              {loading && (
                <Loading
                  size={12}
                  color="#fff"
                  thickness={4}
                  style={{
                    marginRight: '8px'
                  }}
                />
              )}
              {showSuccess && !loading && (
                <CheckmarkSolidCircle
                  color="white"
                  checkMarkColor={colors.darkBlue}
                  style={{
                    marginRight: '8px',
                    height: '15px',
                    width: '15px'
                  }}
                />
              )}
              {showSuccess ? 'Email sent' : 'Contact sales'}
            </SlButton>
          </Flex>
        </SlDialogContent>
      </SlDialog>
    </SlDialogRoot>
  );
};

BeaconBasicUpgradeModal.show = () => {
  useBeaconBasicUpgradeModal.getState().openModal();
};

export default BeaconBasicUpgradeModal;
